import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Machine } from "../services/BackendDataService";
import { getMachineTypeCategory } from "../utils/sapMetadataTranslations";
import {
  applyFlowLayout,
  buildFlowEntries,
  saveFlowLayoutToBackend,
} from "../services/FlowLayoutService";
import { useUnifiedAuth } from "../auth/useUnifiedAuth";
import { useSAPDataStore } from "../store/SAPDataStore";
import { useSettingsStore } from "../store/SettingsStore";
import type { LiveMachineStatus } from "../hooks/useLiveLineStatus";
import { animate } from "animejs";

type MachineStatus =
  | "ok"
  | "running"
  | "warning"
  | "error"
  | "stopped"
  | "unknown";

function getStatus(machine: Machine): MachineStatus {
  if (machine.status === "error") return "error";
  if (machine.status === "maintenance") return "warning";
  if (machine.status === "stopped") return "stopped";
  if (machine.datenVerfuegbar) return "ok";
  return "unknown";
}

const STATUS_STYLES: Record<
  MachineStatus,
  { border: string; text: string; bg: string; number: string }
> = {
  ok: {
    border: "border-[#0064C8]",
    text: "text-gray-700",
    bg: "bg-[#F2F8FC]",
    number: "text-[#0064C8]",
  },
  running: {
    border: "border-emerald-500",
    text: "text-gray-700",
    bg: "bg-emerald-50",
    number: "text-emerald-600",
  },
  warning: {
    border: "border-orange-400",
    text: "text-gray-700",
    bg: "bg-[#F2F8FC]",
    number: "text-orange-500",
  },
  error: {
    border: "border-red-500",
    text: "text-gray-700",
    bg: "bg-[#F2F8FC]",
    number: "text-red-500",
  },
  stopped: {
    border: "border-gray-400",
    text: "text-gray-400",
    bg: "bg-gray-50",
    number: "text-gray-400",
  },
  unknown: {
    border: "border-gray-300",
    text: "text-gray-500",
    bg: "bg-[#F2F8FC]",
    number: "text-gray-500",
  },
};

function StatusBadge({ status }: { status: MachineStatus }) {
  if (status === "ok" || status === "running" || status === "unknown")
    return null;

  if (status === "error") {
    return (
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center shadow-md z-10">
        <svg
          className="w-3 h-3 text-white"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    );
  }

  if (status === "warning") {
    return (
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-5 bg-orange-400 rounded-md flex items-center justify-center shadow-md z-10 rotate-0">
        <svg
          className="w-3.5 h-3.5 text-white"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    );
  }

  if (status === "stopped") {
    return (
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-5 bg-gray-400 rounded-full flex items-center justify-center shadow-md z-10">
        <svg
          className="w-3 h-3 text-white"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    );
  }

  return null;
}

function MachineNode({
  machine,
  lineId,
  lineName,
  factory,
  liveStatus,
}: {
  machine: Machine;
  lineId?: string;
  lineName?: string;
  factory?: string;
  liveStatus?: LiveMachineStatus;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const nodeRef = useRef<HTMLButtonElement>(null);
  const borderPathRef = useRef<SVGRectElement>(null);
  const prevStatusRef = useRef<string | null>(null);

  // Use live status if available, otherwise fall back to static status
  const staticStatus = getStatus(machine);
  const effectiveStatus: MachineStatus = liveStatus
    ? liveStatus.isRunning
      ? "running"
      : "warning"
    : staticStatus;
  const s = STATUS_STYLES[effectiveStatus];

  // Animate on status change: SVG path trace + glow
  useEffect(() => {
    if (!liveStatus || !nodeRef.current || !borderPathRef.current) return;
    const currentStatusName = liveStatus.statusName;
    if (
      prevStatusRef.current !== null &&
      prevStatusRef.current !== currentStatusName
    ) {
      const path = borderPathRef.current;
      const color = liveStatus.isRunning ? "#10b981" : "#f59e0b";

      // Get path length for stroke-dasharray animation
      const length = path.getTotalLength();
      path.style.strokeDasharray = `${length}`;
      path.style.strokeDashoffset = `${length}`;
      path.style.stroke = color;
      path.style.opacity = "1";

      // Phase 1: Draw the border path around the box
      animate(path, {
        strokeDashoffset: [length, 0],
        duration: 700,
        ease: "inOutQuad",
        onComplete: () => {
          // Phase 2: Glow pulse at the end
          animate(nodeRef.current!, {
            boxShadow: [
              `0 0 0px 0px ${color}00`,
              `0 0 24px 8px ${color}88`,
              `0 0 0px 0px ${color}00`,
            ],
            duration: 800,
            ease: "outQuad",
          });
          // Fade out the SVG path trace
          animate(path, {
            opacity: [1, 0],
            duration: 600,
            delay: 200,
            ease: "outQuad",
          });
        },
      });

      // Scale bump
      animate(nodeRef.current, {
        scale: [1, 1.06, 1],
        duration: 500,
        delay: 400,
        ease: "outBack(1.4)",
      });
    }
    prevStatusRef.current = currentStatusName;
  }, [liveStatus?.statusName]);

  // bautyp = "9392 - Modell 409", machineName = type like "Case Packer"
  const machineNum = machine.maschinennummer || machine.maschineBK || "";
  let rawType = machine.bautyp || machine.machineType || "";
  // Strip machine number prefix (e.g. "9392 - Modell 409" → "Modell 409")
  if (rawType && machineNum && rawType.startsWith(machineNum)) {
    rawType = rawType
      .replace(new RegExp(`^${machineNum}\\s*[-–]?\\s*`), "")
      .trim();
  }
  // Strip "Modell " prefix (e.g. "Modell 409" → "409")
  const modelName = rawType.replace(/^Modell\s+/i, "");
  // Get translated type category (e.g. "packer", "wrapper", "case_packer")
  const typeCategory = getMachineTypeCategory(
    machine.bautyp || machine.machineType || "",
  );
  const typeName =
    typeCategory !== "unknown"
      ? t(`machineTypeCategories.${typeCategory}`, typeCategory)
      : "";

  const handleClick = () => {
    const id = machine.maschinennummer || machine.maschineBK;
    navigate(`/machine/${id}`, {
      state: {
        machineName: machine.machineName || modelName,
        lineId,
        lineName,
        factory,
        machineType: machine.bautyp || machine.machineType,
      },
    });
  };

  return (
    <div className="relative flex flex-col items-center flex-shrink-0">
      <button
        ref={nodeRef}
        onClick={handleClick}
        className={`
          relative flex flex-col items-center justify-center
          w-[60px] h-[52px] sm:w-[68px] sm:h-[58px] md:w-[76px] md:h-[64px] lg:w-[96px] lg:h-[80px] xl:w-[110px] xl:h-[92px]
          rounded-[12px] lg:rounded-[16px] border-2 lg:border-[3px] ${s.border} ${s.bg}
          shadow-lg hover:shadow-xl
          cursor-pointer transition-all duration-300
          hover:brightness-95 active:scale-95
          focus:outline-none focus:ring-2 focus:ring-on-header/50
        `}
      >
        {/* SVG border trace animation overlay */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <rect
            ref={borderPathRef}
            x="1"
            y="1"
            width="98"
            height="98"
            rx="14"
            fill="none"
            strokeWidth="4"
            strokeLinecap="round"
            style={{ opacity: 0 }}
          />
        </svg>
        <span
          className={`text-[8px] sm:text-[9px] md:text-[10px] lg:text-xs font-bold ${s.text} leading-tight text-center px-0.5`}
        >
          {modelName || "Machine"}
        </span>
        {typeName && (
          <span
            className={`text-[7px] sm:text-[7px] md:text-[8px] lg:text-[9px] ${s.text} opacity-60 leading-tight text-center px-0.5`}
          >
            {typeName}
          </span>
        )}
        <span
          className={`text-[8px] sm:text-[9px] md:text-[10px] lg:text-xs font-bold mt-0.5 ${s.number}`}
        >
          {machineNum}
        </span>
        <StatusBadge status={effectiveStatus} />
      </button>
    </div>
  );
}

function Connector({ blue }: { blue?: boolean }) {
  return (
    <div className="flex items-center flex-shrink-0">
      <div
        className={`w-2 sm:w-2.5 md:w-3 lg:w-5 h-[2px] lg:h-[3px] rounded-full ${blue ? "bg-[#0064C8]" : "bg-on-header/40"}`}
      />
    </div>
  );
}

interface MachineFlowChainProps {
  machines: Machine[];
  lineId?: string;
  lineName?: string;
  factory?: string;
  compact?: boolean;
  liveStatuses?: Map<string, LiveMachineStatus>;
}

/** Group machines by flowGroup for rendering parallel stacks */
function groupMachines(machines: Machine[]): Machine[][] {
  const groups: Map<number, Machine[]> = new Map();
  for (const m of machines) {
    const g = m.flowGroup ?? -1;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(m);
  }
  // Sort groups by the minimum flowOrder in each group
  return Array.from(groups.values()).sort(
    (a, b) => (a[0].flowOrder ?? 0) - (b[0].flowOrder ?? 0),
  );
}

/** Check if previous group has at least one machine with data */
function prevGroupHasData(groups: Machine[][], groupIndex: number): boolean {
  if (groupIndex <= 0) return false;
  return groups[groupIndex - 1].some((m) => getStatus(m) === "ok");
}

// ─── Edit Mode Node ──────────────────────────────────────────────
function EditMachineNode({
  machine,
  isDragging,
  isParallel,
  onDragStart,
  onDragOver,
  onDrop,
  onToggleParallel,
}: {
  machine: Machine;
  isDragging: boolean;
  isParallel: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onToggleParallel: () => void;
}) {
  const { t } = useTranslation();
  const status = getStatus(machine);
  const s = STATUS_STYLES[status];

  const machineNum = machine.maschinennummer || machine.maschineBK || "";
  let rawType = machine.bautyp || machine.machineType || "";
  if (rawType && machineNum && rawType.startsWith(machineNum)) {
    rawType = rawType
      .replace(new RegExp(`^${machineNum}\\s*[-–]?\\s*`), "")
      .trim();
  }
  const modelName = rawType.replace(/^Modell\s+/i, "");
  const typeCategory = getMachineTypeCategory(
    machine.bautyp || machine.machineType || "",
  );
  const typeName =
    typeCategory !== "unknown"
      ? t(`machineTypeCategories.${typeCategory}`, typeCategory)
      : "";

  return (
    <div
      className={`relative flex flex-col items-center flex-shrink-0 transition-opacity ${isDragging ? "opacity-30" : ""}`}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div
        className={`
          relative flex flex-col items-center justify-center
          w-[56px] h-[48px] sm:w-[64px] sm:h-[54px] md:w-[72px] md:h-[60px] lg:w-[88px] lg:h-[72px]
          rounded-[10px] lg:rounded-[14px] border-2 ${s.border} ${s.bg}
          shadow-md cursor-grab active:cursor-grabbing
          ring-1 ring-on-header/40
        `}
      >
        <span
          className={`text-[7px] sm:text-[8px] md:text-[9px] lg:text-[11px] font-bold ${s.text} leading-tight text-center px-0.5`}
        >
          {modelName || "Machine"}
        </span>
        {typeName && (
          <span
            className={`text-[6px] sm:text-[6px] md:text-[7px] lg:text-[8px] ${s.text} opacity-60 leading-tight text-center px-0.5`}
          >
            {typeName}
          </span>
        )}
        <span
          className={`text-[7px] sm:text-[8px] md:text-[9px] lg:text-[11px] font-bold mt-0.5 ${s.number}`}
        >
          {machineNum}
        </span>
      </div>
      {/* Toggle parallel button */}
      <button
        onClick={onToggleParallel}
        className={`mt-0.5 text-[7px] lg:text-[8px] px-1.5 py-0.5 rounded transition-colors ${
          isParallel
            ? "bg-blue-400/40 hover:bg-blue-400/60 text-on-header"
            : "bg-on-header/15 hover:bg-on-header/30 text-on-header/70"
        }`}
        title={
          isParallel ? "Ungroup (serial)" : "Group with previous (parallel)"
        }
      >
        {isParallel ? "↕" : "║"}
      </button>
    </div>
  );
}

const MachineFlowChain: React.FC<MachineFlowChainProps> = ({
  machines,
  lineId,
  lineName,
  factory,
  compact,
  liveStatuses,
}) => {
  const { t: _t } = useTranslation();
  const { token } = useUnifiedAuth();
  const sapLastFetched = useSAPDataStore((s) => s.lastFetched);
  const forceRefresh = useSAPDataStore((s) => s.forceRefresh);
  const demoMode = useSettingsStore((s) => s.demoMode);
  const [editing, setEditing] = useState(false);
  const [editMachines, setEditMachines] = useState<Machine[]>([]);
  const dragIndexRef = useRef<number>(-1);

  const [layoutVersion, setLayoutVersion] = useState(0);

  // Apply saved layout on mount or when machines/layout change. SAP order is the
  // master; the local overlay is only used while it's newer than the SAP data
  // (applyFlowLayout decides based on sapLastFetched).
  const layoutMachines = React.useMemo(() => {
    if (!machines || machines.length === 0) return [];
    return applyFlowLayout(lineId || "", machines, sapLastFetched);
  }, [machines, lineId, layoutVersion, sapLastFetched]);

  if (!machines || machines.length === 0) return null;

  // In view mode, only show machines with data; in edit mode, show all
  const displayMachines = editing
    ? editMachines
    : layoutMachines.filter((m) => m.datenVerfuegbar);
  const groups = groupMachines(displayMachines);

  // ─── Edit handlers ──────────────────────────────────────────
  const startEditing = () => {
    setEditMachines(
      layoutMachines.map((m, i) => ({
        ...m,
        flowOrder: m.flowOrder ?? i,
        flowGroup: m.flowGroup ?? i,
      })),
    );
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditMachines([]);
  };

  const saveLayout = () => {
    const entries = buildFlowEntries(editMachines);
    setEditing(false);

    if (!lineId) return;

    if (!token) {
      console.warn("⚠️ FlowLayout: no token — cannot save order to backend.");
      return;
    }

    // Persist to the backend, then pull fresh SAP data so the persisted order
    // becomes authoritative for everyone. SAP is the single source of truth —
    // there is no local overlay. The editor will see the new order once SAP
    // returns it (typically <1s).
    void (async () => {
      const ok = await saveFlowLayoutToBackend(lineId, entries, token);
      if (ok) {
        try {
          await forceRefresh(token);
          setLayoutVersion((v) => v + 1);
        } catch (err) {
          console.warn("⚠️ FlowLayout: SAP refresh after save failed:", err);
        }
      } else {
        console.error("❌ FlowLayout: backend did not persist the order.");
      }
    })();
  };

  // Drag-to-reorder
  const handleDragStart = (index: number) => (e: React.DragEvent) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (_index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (targetIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    const fromIndex = dragIndexRef.current;
    if (fromIndex < 0 || fromIndex === targetIndex) return;

    const newMachines = [...editMachines];
    const [moved] = newMachines.splice(fromIndex, 1);
    newMachines.splice(targetIndex, 0, moved);

    // Reassign flowOrder
    setEditMachines(newMachines.map((m, i) => ({ ...m, flowOrder: i })));
    dragIndexRef.current = -1;
  };

  // Make machine parallel with the previous one (same flowGroup)
  const toggleParallel = (index: number) => () => {
    if (index <= 0) return;
    const newMachines = [...editMachines];
    const current = newMachines[index];
    const prev = newMachines[index - 1];
    if (current.flowGroup === prev.flowGroup) {
      // Already parallel — ungroup (make serial)
      const maxGroup = Math.max(...newMachines.map((m) => m.flowGroup ?? 0));
      newMachines[index] = { ...current, flowGroup: maxGroup + 1 };
    } else {
      // Group with previous
      newMachines[index] = { ...current, flowGroup: prev.flowGroup };
    }
    setEditMachines(newMachines);
  };

  const isParallel = (index: number): boolean => {
    if (index <= 0) return false;
    return editMachines[index].flowGroup === editMachines[index - 1].flowGroup;
  };

  // ─── Edit Mode Render ──────────────────────────────────────
  if (editing) {
    const editGroups = groupMachines(editMachines);
    return (
      <div className="flex flex-col items-center gap-1 overflow-hidden">
        {/* Edit chain */}
        <div className="flex items-center flex-nowrap py-1 gap-0">
          {editGroups.map((group, gi) => (
            <React.Fragment key={gi}>
              {gi > 0 && <Connector blue={prevGroupHasData(editGroups, gi)} />}
              {group.length === 1 ? (
                <EditMachineNode
                  machine={group[0]}
                  isDragging={
                    dragIndexRef.current === editMachines.indexOf(group[0])
                  }
                  isParallel={isParallel(editMachines.indexOf(group[0]))}
                  onDragStart={handleDragStart(editMachines.indexOf(group[0]))}
                  onDragOver={handleDragOver(editMachines.indexOf(group[0]))}
                  onDrop={handleDrop(editMachines.indexOf(group[0]))}
                  onToggleParallel={toggleParallel(
                    editMachines.indexOf(group[0]),
                  )}
                />
              ) : (
                <div className="flex flex-col items-center gap-0.5 bg-on-header/10 rounded-xl px-1 py-0.5">
                  {group.map((m) => {
                    const idx = editMachines.indexOf(m);
                    return (
                      <EditMachineNode
                        key={m.maschinennummer || m.maschineBK}
                        machine={m}
                        isDragging={dragIndexRef.current === idx}
                        isParallel={isParallel(idx)}
                        onDragStart={handleDragStart(idx)}
                        onDragOver={handleDragOver(idx)}
                        onDrop={handleDrop(idx)}
                        onToggleParallel={toggleParallel(idx)}
                      />
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        {/* Toolbar below */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={saveLayout}
            className="flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] lg:text-xs font-semibold px-2 lg:px-3 py-1 rounded-md shadow transition-colors"
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            Save
          </button>
          <button
            onClick={cancelEditing}
            className="flex items-center gap-1 bg-on-header/20 hover:bg-on-header/30 text-on-header text-[10px] lg:text-xs font-semibold px-2 lg:px-3 py-1 rounded-md shadow transition-colors"
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ─── View Mode Render ──────────────────────────────────────
  return (
    <div className="relative group overflow-hidden">
      {/* Edit button — hidden in demo mode, where saving is blocked anyway */}
      {!compact && lineId && !demoMode && (
        <button
          onClick={startEditing}
          className="absolute -top-1 -right-1 z-10 opacity-0 group-hover:opacity-100 bg-on-header/20 hover:bg-on-header/40 text-on-header rounded-full p-1 transition-all duration-200 shadow"
          title="Edit flow layout"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
            />
          </svg>
        </button>
      )}
      <div
        className={`flex items-center flex-nowrap ${compact ? "py-0.5 gap-0" : "py-1 gap-0"}`}
      >
        {groups.map((group, gi) => (
          <React.Fragment key={gi}>
            {gi > 0 && (
              <div
                className="opacity-0 animate-[fadeIn_0.3s_ease-out_forwards]"
                style={{ animationDelay: `${gi * 100 + 50}ms` }}
              >
                <Connector blue={prevGroupHasData(groups, gi)} />
              </div>
            )}
            {group.length === 1 ? (
              <div
                className="opacity-0 animate-[fadeSlideIn_0.4s_ease-out_forwards]"
                style={{ animationDelay: `${gi * 100}ms` }}
              >
                <MachineNode
                  machine={group[0]}
                  lineId={lineId}
                  lineName={lineName}
                  factory={factory}
                  liveStatus={liveStatuses?.get(
                    group[0].maschinennummer || group[0].maschineBK || "",
                  )}
                />
              </div>
            ) : (
              /* Parallel group: stack vertically */
              <div
                className="flex flex-col items-center gap-0.5 opacity-0 animate-[fadeSlideIn_0.4s_ease-out_forwards]"
                style={{ animationDelay: `${gi * 100}ms` }}
              >
                {group.map((m) => (
                  <MachineNode
                    key={m.maschinennummer || m.maschineBK}
                    machine={m}
                    lineId={lineId}
                    lineName={lineName}
                    factory={factory}
                    liveStatus={liveStatuses?.get(
                      m.maschinennummer || m.maschineBK || "",
                    )}
                  />
                ))}
              </div>
            )}
          </React.Fragment>
        ))}
        <style>{`
          @keyframes fadeSlideIn {
            from { opacity: 0; transform: translateY(12px) scale(0.9); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default MachineFlowChain;
